/**
 * Host switching — deciding whether an authenticated session belongs on a
 * different origin than the one it is currently on.
 *
 * Deployed builds pin the session to a known set of origins: `VITE_MAIN_URLS`
 * for ordinary users and `VITE_NEXT_URLS` for users on the `next` flag. When
 * the current origin is absent from the relevant allowlist, the app bounces the
 * browser to the first entry, preserving the path.
 *
 * The allowlists come from build-time env vars, so they are absent in any build
 * that does not set them — a local dev server or Playwright run started without
 * `src/web-app/.env` (it is gitignored, so it does not follow a checkout into a
 * new git worktree). An unset allowlist must mean "no host switching is
 * configured here", never "switch to the empty string": concatenating `''` with
 * the pathname produced a same-origin navigation to `/` on every boot, an
 * unbreakable redirect loop that left the app stuck on its root spinner.
 *
 * Hence the single invariant this module enforces: we only ever navigate away
 * to an absolute http(s) origin drawn from the allowlist. Anything else —
 * unset, empty, whitespace, or a relative value — is a misconfiguration, and
 * the safe response to a misconfigured allowlist is to stay put.
 */

/** Split a comma-separated allowlist into trimmed, non-empty entries. */
export function parseOriginAllowlist(allowlist: string | undefined | null): string[] {
  return (allowlist ?? '')
    .split(',')
    .map(url => url.trim())
    .filter(url => url.length > 0);
}

/** True when `origin` is listed in the comma-separated `allowlist`. */
export function isOriginInList(allowlist: string | undefined | null, origin: string): boolean {
  return parseOriginAllowlist(allowlist).includes(origin);
}

/**
 * An entry is only usable as a redirect target if it is an absolute http(s)
 * URL. This is what stops a blank or relative entry from being concatenated
 * with the current pathname into a same-origin navigation.
 */
function isAbsoluteHttpOrigin(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.href === `${parsed.origin}/`
    );
  } catch {
    return false;
  }
}

export interface HostSwitchTargetInput {
  /** Raw comma-separated allowlist, e.g. `import.meta.env.VITE_MAIN_URLS`. */
  allowlist: string | undefined | null;
  /** The origin the browser is on now, e.g. `window.location.origin`. */
  currentOrigin: string;
  /** Path to carry over — pathname + search + hash. */
  targetPath: string;
}

/**
 * Resolve the absolute URL the browser should be sent to, or `null` when it
 * should stay where it is.
 *
 * Returns `null` when:
 *   - the allowlist is unset, empty, or has no usable entries (not configured);
 *   - the current origin is already allowed (nothing to do);
 *   - no entry is an absolute http(s) origin (misconfigured — stay put).
 */
export function resolveHostSwitchTarget({
  allowlist,
  currentOrigin,
  targetPath,
}: HostSwitchTargetInput): string | null {
  const allowedOrigins = parseOriginAllowlist(allowlist);

  // Not configured for this build — host switching is off, not "go to ''".
  if (allowedOrigins.length === 0) return null;

  if (
    allowedOrigins.some(
      origin => isAbsoluteHttpOrigin(origin) && new URL(origin).origin === currentOrigin,
    )
  )
    return null;

  const destination = allowedOrigins.find(isAbsoluteHttpOrigin);
  if (!destination) return null;

  return destination.replace(/\/+$/, '') + targetPath;
}

/*
 * Loop guard (GDN-4167).
 *
 * Auth state is per-origin: the Auth0 SPA cache and refresh token live in each
 * origin's localStorage. Logging out on one origin leaves the other origin's
 * session intact, so after "staff on next → log out → log in as a non-staff
 * user" the two origins disagree about who is signed in. next resolves the new
 * user (`next=false`) and sends the browser to app; app still resolves the
 * stale staff session (`next=true`) and sends it straight back — each origin is
 * self-consistent, and together they ping-pong forever.
 *
 * Two layers stop that:
 *   1. Every host switch carries a marker query param whose value is a short
 *      hash of the sender's user id. The receiving origin stashes it in
 *      sessionStorage at boot. If that origin then wants to bounce the browser
 *      straight back:
 *        - and it is signed in as a different user than the sender, the two
 *          origins hold different sessions. It never redirects back; the
 *          caller forces a full logout here so the next login decides. This
 *          is usually the stale session, but not always (the browser may have
 *          started on the stale origin); either way the loop ends.
 *        - otherwise (same user, or an unhashed legacy marker), it drops its
 *          local auth cache and re-authenticates, so the decision comes from
 *          the identity Auth0's SSO session currently holds.
 *   2. A per-origin hop budget. If this origin has already redirected away
 *      `HOST_SWITCH_MAX_HOPS` times inside `HOST_SWITCH_HOP_WINDOW_MS`, it stops
 *      redirecting and the caller forces a full logout. This bounds the loop
 *      even if re-auth somehow fails to reconcile the two origins.
 */

/** Query param appended to every host-switch redirect. */
export const HOST_SWITCH_PARAM = 'jot_host_switch';

/** How long an arrival marker counts as "we were just sent here". */
export const HOST_SWITCH_ARRIVAL_TTL_MS = 60_000;

/** Window over which outbound host switches are counted. */
export const HOST_SWITCH_HOP_WINDOW_MS = 120_000;

/** Max outbound host switches from one origin within the hop window. */
export const HOST_SWITCH_MAX_HOPS = 3;

const ARRIVAL_KEY = 'jot:hostSwitch:arrivedAt';
const HOPS_KEY = 'jot:hostSwitch:hops';
const ARRIVAL_USER_KEY = 'jot:hostSwitch:arrivedFromUser';
const REAUTH_KEY = 'jot:hostSwitch:reauthAt';

/** Marker value when the sender has no user id to hash. */
const ANONYMOUS_MARKER = '1';

/** Shape of a `hashHostSwitchUser` result; anything else is treated as legacy. */
const USER_HASH_PATTERN = /^[0-9a-f]{8}$/;

/** The subset of `Storage` the guard needs — `sessionStorage` in the app. */
export type HostSwitchStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const NOOP_STORAGE: HostSwitchStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

/**
 * `window.sessionStorage` itself throws when storage is blocked (e.g. a
 * SecurityError), and a default parameter is evaluated before the body's
 * try/catch can help. Fall back to a no-op store so the guard degrades to
 * plain redirects instead of crashing boot.
 */
function getHostSwitchStorage(): HostSwitchStorage {
  try {
    return window.sessionStorage;
  } catch {
    return NOOP_STORAGE;
  }
}

function safeGet(storage: HostSwitchStorage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: HostSwitchStorage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota) — the guard degrades to a no-op.
  }
}

function safeRemove(storage: HostSwitchStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // See safeSet.
  }
}

function isRecent(raw: string | null, now: number, ttlMs: number): boolean {
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && now - at >= 0 && now - at < ttlMs;
}

function readHops(storage: HostSwitchStorage, now: number): number[] {
  const raw = safeGet(storage, HOPS_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (at): at is number =>
        typeof at === 'number' && now - at >= 0 && now - at < HOST_SWITCH_HOP_WINDOW_MS,
    );
  } catch {
    return [];
  }
}

/** Forget this origin's outbound hops, e.g. before a forced logout. */
export function clearHostSwitchHops(storage: HostSwitchStorage = getHostSwitchStorage()): void {
  safeRemove(storage, HOPS_KEY);
}

/**
 * Short, stable hash of a user id (32-bit FNV-1a, hex) so the marker can say
 * who sent the browser without putting the raw id in the URL. It only has to
 * tell two accounts in one browser apart; it is not a security boundary.
 */
export function hashHostSwitchUser(userId: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < userId.length; i++) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Add the host-switch marker to a redirect target, keeping its hash intact.
 * The marker value identifies the signed-in user so the receiving origin can
 * tell whether it holds the same session.
 */
export function withHostSwitchMarker(target: string, userId?: string | null): string {
  const url = new URL(target);
  url.searchParams.set(HOST_SWITCH_PARAM, userId ? hashHostSwitchUser(userId) : ANONYMOUS_MARKER);
  return url.toString();
}

/** Remove the host-switch marker from a `search` string (`?a=1&b=2` form). */
export function stripHostSwitchMarker(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(HOST_SWITCH_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * Call once at boot, before the router can rewrite the URL. If this page load
 * is the landing side of a host switch, record that in sessionStorage and strip
 * the marker from the address bar so it never leaks into app routes, bookmarks,
 * or the next outbound redirect.
 */
export function captureHostSwitchArrival(
  win: Pick<Window, 'location' | 'history'> = window,
  storage: HostSwitchStorage = getHostSwitchStorage(),
  now: number = Date.now(),
): void {
  const params = new URLSearchParams(win.location.search);
  if (!params.has(HOST_SWITCH_PARAM)) {
    // Any unmarked full page load (a reload, or the Auth0 `/callback` after a
    // login) ends the "just arrived" window: the next decision is made by a
    // session established here, not by the one that sent us.
    safeRemove(storage, ARRIVAL_KEY);
    safeRemove(storage, ARRIVAL_USER_KEY);
    return;
  }

  safeSet(storage, ARRIVAL_KEY, String(now));
  const sender = params.get(HOST_SWITCH_PARAM);
  if (sender && USER_HASH_PATTERN.test(sender)) safeSet(storage, ARRIVAL_USER_KEY, sender);
  else safeRemove(storage, ARRIVAL_USER_KEY);

  const { pathname, hash } = win.location;
  try {
    win.history.replaceState(
      win.history.state,
      '',
      pathname + stripHostSwitchMarker(win.location.search) + hash,
    );
  } catch {
    // Leaving the marker in the URL is harmless; the guard state is recorded.
  }
}

export type HostSwitchDecision =
  | { action: 'stay'; reason: 'on_target_origin' | 'hop_budget_exhausted' | 'session_mismatch' }
  | { action: 'redirect'; url: string }
  | { action: 'reauthenticate' };

export interface HostSwitchDecisionInput {
  /** Result of `resolveHostSwitchTarget` — `null` means this origin is right. */
  target: string | null;
  /** The user this origin is signed in as, from `getUserInfo`. */
  userId?: string | null;
  storage?: HostSwitchStorage;
  now?: number;
}

/**
 * Decide what to do with a resolved host-switch target, applying the loop
 * guard. Records its own bookkeeping in `storage`, so call it once per
 * decision, not speculatively.
 */
export function decideHostSwitch({
  target,
  userId,
  storage = getHostSwitchStorage(),
  now = Date.now(),
}: HostSwitchDecisionInput): HostSwitchDecision {
  const justArrived = isRecent(safeGet(storage, ARRIVAL_KEY), now, HOST_SWITCH_ARRIVAL_TTL_MS);
  const sender = justArrived ? safeGet(storage, ARRIVAL_USER_KEY) : null;
  // The arrival marker answers one question — "is this the first decision
  // after being sent here?" — so it is spent by the first decision either way.
  safeRemove(storage, ARRIVAL_KEY);
  safeRemove(storage, ARRIVAL_USER_KEY);

  if (!target) return { action: 'stay', reason: 'on_target_origin' };

  if (sender && userId && sender !== hashHostSwitchUser(userId)) {
    // The other origin is signed in as someone else and just sent us here.
    // Sending the browser back would only start the ping-pong, so stay and
    // let the caller sign this session out.
    return { action: 'stay', reason: 'session_mismatch' };
  }

  if (justArrived) {
    // The other origin just sent us here, and we want to send the browser
    // straight back: the two origins hold different sessions. Re-authenticate
    // instead — but only once per window, so a re-auth that resolves to the
    // same stale answer falls through to the hop budget below rather than
    // looping through Auth0.
    if (!isRecent(safeGet(storage, REAUTH_KEY), now, HOST_SWITCH_HOP_WINDOW_MS)) {
      safeSet(storage, REAUTH_KEY, String(now));
      return { action: 'reauthenticate' };
    }
  }

  const hops = readHops(storage, now);
  if (hops.length >= HOST_SWITCH_MAX_HOPS)
    return { action: 'stay', reason: 'hop_budget_exhausted' };

  safeSet(storage, HOPS_KEY, JSON.stringify([...hops, now]));
  return { action: 'redirect', url: withHostSwitchMarker(target, userId) };
}
